import type {
  AuthUser,
  BackupJobSummary,
  BackupTargetConfig,
  CounterProfileRecord,
  DashboardResponse,
  EntityRecord,
  EntitySettingsRecord,
  EntitySummary,
  EntityWorkspaceBundle,
  MasterOverviewResponse,
  OutletRecord,
  RequiredDocumentTemplateRecord,
  ResortRecord,
  StocktakeSummary,
  StorageUsageReport,
} from "../../shared/domain";

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: "include",
    headers: {
      ...(init?.body instanceof FormData ? {} : { "content-type": "application/json" }),
      ...init?.headers,
    },
    ...init,
  });
  if (!response.ok) {
    const error = (await response.json().catch(() => ({ error: "Request failed." }))) as { error?: string };
    throw new Error(error.error ?? "Request failed.");
  }
  const payload = (await response.json()) as { data: T };
  return payload.data as T;
}

export const api = {
  me: () => request<AuthUser | null>("/api/auth/me"),
  login: (email: string, password: string) =>
    request<{ ok: boolean }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<{ ok: boolean }>("/api/auth/logout", { method: "POST" }),
  switchWorkspace: (mode: "master" | "entity", entityId?: string | null) =>
    request<{ ok: boolean }>("/api/auth/workspace", {
      method: "POST",
      body: JSON.stringify({ mode, entityId }),
    }),
  startImpersonation: (entityId: string) =>
    request<{ ok: boolean }>("/api/master/impersonation", {
      method: "POST",
      body: JSON.stringify({ entityId }),
    }),
  stopImpersonation: () => request<{ ok: boolean }>("/api/master/impersonation", { method: "DELETE" }),

  masterOverview: () => request<MasterOverviewResponse>("/api/master/overview"),
  masterEntities: () => request<EntitySummary[]>("/api/master/entities"),
  saveEntity: (entity: Partial<EntityRecord> & { name: string; shortCode: string }, settings?: Partial<EntitySettingsRecord>) =>
    request<EntityRecord>("/api/master/entities", {
      method: "POST",
      body: JSON.stringify({ entity, settings }),
    }),
  masterStorage: () => request<StorageUsageReport>("/api/master/storage"),
  masterBackups: () =>
    request<{ target: BackupTargetConfig | null; jobs: BackupJobSummary[] }>("/api/master/backups"),
  saveBackupTarget: (body: {
    tenantId?: string;
    clientId?: string;
    clientSecret?: string;
    driveId?: string;
    rootFolder?: string;
    schedule?: string;
    enabled?: boolean;
  }) =>
    request<BackupTargetConfig>("/api/master/backups/target", {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  runBackup: (entityId: string) =>
    request<BackupJobSummary>("/api/master/backups/run", {
      method: "POST",
      body: JSON.stringify({ entityId }),
    }),

  entitySetup: (entityId: string) => request<EntityWorkspaceBundle>(`/api/entities/${entityId}/setup`),
  saveEntitySettings: (entityId: string, body: Partial<EntitySettingsRecord>) =>
    request<EntitySettingsRecord>(`/api/entities/${entityId}/settings`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  saveResort: (entityId: string, body: Partial<ResortRecord> & { code: string; name: string }) =>
    request<ResortRecord>(`/api/entities/${entityId}/resorts`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  saveOutlet: (entityId: string, body: Partial<OutletRecord> & { resortId: string; code: string; name: string; active?: boolean }) =>
    request<OutletRecord>(`/api/entities/${entityId}/outlets`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  saveDocuments: (entityId: string, body: RequiredDocumentTemplateRecord[]) =>
    request<RequiredDocumentTemplateRecord[]>(`/api/entities/${entityId}/documents`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  saveCounters: (entityId: string, body: CounterProfileRecord[]) =>
    request<CounterProfileRecord[]>(`/api/entities/${entityId}/counters`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  saveEntityUser: (
    entityId: string,
    body: {
      id?: string;
      email: string;
      name: string;
      password?: string;
      globalRole?: "master_admin" | "master_ops" | "master_readonly" | null;
      isDefault: boolean;
      assignments: Array<{
        role: "entity_admin" | "resort_ops" | "regional_manager" | "oan" | "payroll" | "entity_readonly";
        scopeType: "entity" | "resort" | "outlet";
        scopeId?: string | null;
      }>;
    }
  ) =>
    request<{ ok: boolean }>(`/api/entities/${entityId}/users`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  dashboard: () => request<DashboardResponse>("/api/dashboard"),
  cycles: (cycleId?: string) =>
    request<{ cycles: Array<{ id: string; label: string; code: string }>; summaries: StocktakeSummary[] }>(
      `/api/cycles${cycleId ? `?cycleId=${cycleId}` : ""}`
    ),
  stocktake: (stocktakeId: string) =>
    request<StocktakeSummary & { aiSummary: { mode: "ai" | "fallback"; text: string } }>(`/api/stocktakes/${stocktakeId}`),
  stocktakeAiSummary: (stocktakeId: string) =>
    request<{ mode: "ai" | "fallback"; text: string }>(`/api/stocktakes/${stocktakeId}/ai-summary`, {
      method: "POST",
      body: JSON.stringify({}),
    }),
  updateStep: (stocktakeId: string, stepNo: number, body: Record<string, unknown>) =>
    request(`/api/stocktakes/${stocktakeId}/steps/${stepNo}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  upload: (formData: FormData) =>
    request("/api/uploads", {
      method: "POST",
      body: formData,
    }),
  reviews: () => request<StocktakeSummary[]>("/api/reviews"),
  managerReview: (stocktakeId: string, action: "approve" | "reject", reason: string) =>
    request(`/api/reviews/${stocktakeId}/manager`, {
      method: "POST",
      body: JSON.stringify({ action, reason }),
    }),
  oanReview: (stocktakeId: string, action: "verify" | "return", notes: string) =>
    request(`/api/reviews/${stocktakeId}/oan`, {
      method: "POST",
      body: JSON.stringify({ action, notes }),
    }),
  payroll: () => request<StocktakeSummary[]>("/api/payroll"),
  processPayroll: (stocktakeId: string) =>
    request(`/api/payroll/${stocktakeId}/process`, {
      method: "POST",
      body: JSON.stringify({}),
    }),
  archive: (stocktakeId: string) => request(`/api/archive/${stocktakeId}`),
};
